/**
 * ATT-3201 admin half — host-keyed routing cache.
 *
 * `config/tenants.ts#getTenantConfig` is synchronous, but the registry resolve
 * that supplies a flipped tenant's routing fields is async. This module bridges
 * the gap: the async discovery flow (`useTenantDiscovery`) and the server-side
 * NextAuth resolve write resolved routing here, and the sync `getTenantConfig`
 * reads it back, keyed by normalized host.
 *
 * Scope: client-side, this is a single per-process (per-tab) map hydrated once
 * at boot. Server-side, the NextAuth handler maintains its OWN short-TTL cache
 * (see `pages/api/auth/[...nextauth].ts`); this module is the client store and
 * the shared shape definition. Entries are normalized on write and read so the
 * key always matches the `normalizeHost` contract used everywhere else.
 */

import { normalizeHost } from "../../config/tenants";

/**
 * Registry-resolved routing for a host. `cookieDomain`/`publicBase` are
 * nullable because the registry may omit them for a given domain row; callers
 * apply their own fallbacks (the server cookie path fails closed, the client
 * derives a safe publicBase).
 */
export type HostRouting = {
  apiUrl: string;
  cookieDomain: string | null;
  portal: string;
  publicBase: string | null;
};

const cache = new Map<string, HostRouting>();

/** Read the resolved routing for a host. Returns null before population. */
export function getRoutingForHost(host: string): HostRouting | null {
  return cache.get(normalizeHost(host)) ?? null;
}

/** Store the resolved routing for a host. Overwrites any prior entry. */
export function setRoutingForHost(host: string, routing: HostRouting): void {
  cache.set(normalizeHost(host), routing);
}

/** Test-only: clear all entries so cases don't leak host state into each other. */
export function __resetForTests(): void {
  cache.clear();
}
